import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { firstValueFrom, Observable } from "rxjs";
import { DigitalSignature } from "../models/digital-signature.model";
import { RequestCacheService } from "../services/request-cache.service";
import {
  ClubTeamPersonDTO,
  ClubTeamEditDTO,
  ClubTeamPersonFilter,
  ClubTeamFilter,
  ClubTeamViewDTO,
  ClubTeamRole
} from "../models/club-team.model";
import { FilterBaseDTO, EventLeaguesFilter } from "../models/filter.model";
import { FilteredResponseDTO } from "../models/filtered-response.model";
import { FilteredRequestDTO } from "../models/filtered-request.model";
import { RatingSystemCompetitionSportRef } from "../models/rating-system-competition-sport.model";
import { PersonDocument } from "../models/person-document.model";

export const personsFilterPATH = `api/event/account/persons/filter`;

@Injectable({
  providedIn: "root",
})
export class ApiService {


  editingPerson: any | undefined;
  baseUrl: string = environment.APIURL;
  // paidOrders$: BehaviorSubject<any> = new BehaviorSubject(null);

  constructor(private http: HttpClient, private requestCacheService: RequestCacheService) { }

  getWaiverAndInfo(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/waiver-and-info`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  getEventDetails(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/event-details`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  getPublicEvent(eventId: number): Observable<unknown> {
    const path = `api/public/events/${eventId}`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateEventDetails(eventId: number, data: any, logoFile?: File | null): Observable<unknown> {
    const path = `api/events/${eventId}/editor/event-details`;
    const formData = new FormData();
    formData.append("data", new Blob([JSON.stringify(data)], { type: "application/json" }));
    if (logoFile) {
      formData.append("logoFile", logoFile, logoFile.name);
    }
    return this.http.put(`${this.baseUrl}${path}`, formData);
  }

  getEventDatesAndLocation(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/event-dates-location`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateEventDatesAndLocation(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/event-dates-location`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getEventPromoterConfig(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/promoter`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateEventPromoterConfig(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/promoter`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getContactAndLegal(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/contact-and-legal`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateContactAndLegal(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/contact-and-legal`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getRegistrationSettings(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/registration-settings`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateRegistrationSettings(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/registration-settings`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getPurchasePayments(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/purchase-payments`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updatePurchasePayments(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/purchase-payments`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getScheduleTime(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/schedule-time`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateScheduleTime(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/schedule-time`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getNotifications(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/notifications`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateNotifications(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/notifications`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getSecurity(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/security`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateSecurity(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/security`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getSystemControls(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/system-controls`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateSystemControls(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/system-controls`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getRestrictions(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/restrictions`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateRestrictions(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/restrictions`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getEventRatingSystems(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/rating-systems`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  getScoreboard(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/scoreboard`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateScoreboard(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/scoreboard`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getReports(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/reports`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  updateReports(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/reports`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  createEventEditorVenue(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/venue`;
    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  updateEventEditorVenue(eventId: number, data: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/venue`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getPublicRatingSystemsDictionary(
    search: string = "",
    includeRatings: boolean = true,
    includeDependent: boolean = true,
  ): Observable<unknown> {
    const params = {
      page: 0,
      size: 500,
      filter: {
        name: search,
        isIncludeRatings: includeRatings,
        isIncludeDependent: includeDependent,
      },
      "sort": {
        "orders": [
          {
            "direction": "ASC",
            "property": "name"
          }
        ]
      },
      offset: 0,
    };
    const path = "api/public/events/filter/rating-systems";
    return this.http.post(`${this.baseUrl}${path}`, params);
  }

  getPublicSportsDictionary(search: string = ""): Observable<unknown> {
    const params = {
      page: 0,
      size: 1000,
      filter: {
        name: search,
        excludeIds: null,
      },
    };
    const path = "api/public/events/filter/sports";
    return this.http.post(`${this.baseUrl}${path}`, params);
  }

  getPublicStylesDictionary(search: string = "", isIncludeRanks = false): Observable<unknown> {
    const params = {
      page: 0,
      size: 500,
      filter: {
        name: search,
        isIncludeRanks: isIncludeRanks
      },
      offset: 0,
    };
    const path = "api/public/events/filter/styles";
    return this.http.post(`${this.baseUrl}${path}`, params);
  }

  getPublicSeasonPassEvents(search: string = "", excludeIds: number[] = []): Observable<unknown> {
    const params = {
      page: 0,
      size: 500,
      filter: {
        name: search,
        isSeasonPass: true,
        excludeIds: excludeIds.length ? excludeIds : null,
      },
      offset: 0,
    };
    const path = "api/public/events/filter";
    return this.http.post(`${this.baseUrl}${path}`, params);
  }

  getBulkUploads(eventId: number): Observable<unknown> {
    const path = `api/event/integration/aau/membership/${eventId}/bulk-uploads`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  deleteBulkUpload(eventId: number, bulkId: number): Observable<unknown> {
    const path = `api/event/integration/aau/membership/${eventId}/bulk-uploads/${bulkId}`;
    return this.http.delete(`${this.baseUrl}${path}`);
  }

  putWaiverAndInfo(
    eventId: number,
    name: string,
    info: string,
    waiver: string
  ): Observable<unknown> {
    const path = `api/events/${eventId}/editor/waiver-and-info`;
    const data = {
      id: eventId,
      name: name,
      info: info,
      waiver: waiver,
    };
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  getSports(leagueId: number, includeRestricted: boolean = false) {
    const path = `api/leagues/sports/filter`;
    const data = {
      page: 0,
      size: 500,
      filter: {
        eventId: null,
        leagueIds: [leagueId],
        includeRestricted: includeRestricted,
      },
    };
    return this.requestCacheService.post<any>(`${this.baseUrl}${path}`, data, undefined, 1000 * 60 * 60 * 24);
  }

  getLeagueSports(filter: EventLeaguesFilter, page?: number, size?: number) {
    const path = `api/leagues/sports/filter`;
    let data: FilteredRequestDTO<EventLeaguesFilter> = {
      page: page !== undefined ? page : 0,
      size: size !== undefined ? size : 500,
      filter: {
        ...filter
      }
    };
    return this.http.post<FilteredResponseDTO<EventLeaguesFilter, any>>(`${this.baseUrl}${path}`, data);
  }

  getActiveLeagueSports(leagueId: number) {
    const path = `api/leagues/sports/active`;
    return this.http.get<RatingSystemCompetitionSportRef[]>(`${this.baseUrl}${path}`, {
      params: {
        leagueId
      }
    });
  }

  searchDivisionTypesRef(eventId: number, search: string = ""): Observable<any> {
    const path = `api/event/divisions/types/ref/filter`;
    const data = {
      page: 0,
      size: 25,
      filter: {
        search
      }
    };
    const params = new HttpParams().set('eventId', eventId.toString());
    return this.http.post(`${this.baseUrl}${path}`, data, { params });
  }

  searchDivisionsRef(eventId: number, search: string = "", divisionTypeId?: number): Observable<any> {
    const path = `api/event/divisions/ref/filter`;
    const data = {
      page: 0,
      size: 25,
      filter: {
        search
      }
    };
    let params = new HttpParams().set('eventId', eventId.toString());
    if (divisionTypeId) {
      params = params.set('divisionTypeId', divisionTypeId.toString());
    }
    return this.http.post(`${this.baseUrl}${path}`, data, { params });
  }

  searchAAUClubs(leagueId: number | number[], value: string, includePending: boolean, districtZipCode: string | undefined, countries: any, size: number, strict: boolean): Observable<unknown> {
    const path = `api/public/club/filter`;
    const data = {
      page: 0,
      size: size,
      filter: {
        name: value,
        includePending: includePending,
        districtZipCode: districtZipCode,
        eventId: null,
        countryIds: countries,
        leagueIds: strict ? (Array.isArray(leagueId) ? leagueId : [leagueId]) : [],
        excludeOldMemberships: !strict
      },
    };
    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  addClub(data: any) {
    const path = `api/clubs`;
    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  searchClubs(name: string) {
    const path = `api/clubs/filter`;
    const data = {
      page: 0,
      size: 25,
      filter: {
        name,
        // eventId: null,
        // leagueIds: [40],
      },
    };
    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  getAllowedItems(eventId: any) {
    const path = `api/orders/${eventId}/officials/allowed`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  getRegistrationConfig(
    countryId: number,
    sportId: number,
    birthDate: string,
    gender: string
  ) {
    const path = `api/event/account/persons/334091/registration/check-config`;
    const data = {
      birthDate: birthDate,
      countryId: countryId,
      sportId: sportId,
      gender: gender,
    };
    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  getPerson(
    updateId: number,
    eventId: number | null,
    minAge: number,
    nonAthletesOnly?: boolean
  ): Observable<unknown> {
    const data: any = {
      size: 3000,
      filter: {
        id: updateId, // not used on the backend (account id is being reset from session)
        excludeIds: [0],
        minAge,
      },
      offset: 0,
    };

    if (eventId) {
      data.filter.eventId = eventId;
    }

    if (nonAthletesOnly) {
      data["filter"]["membershipCategory"] = "NON_ATHLETE";
    }

    return this.requestCacheService.post(`${this.baseUrl}${personsFilterPATH}`, data, undefined, 1000 * 60 * 5);
  }

  getRegistrationOfficials(
    eventId: number,
    officialType: 'COACH' | 'OFFICIAL' | 'VOLUNTEER' | 'REFEREE',
    page: number,
    size: number,
    sort?: { sortBy: string; sortDir: "ASC" | "DESC" },
    subEventId?: number | null
  ): Observable<unknown> {
    const path = `api/reports/events/${eventId}/registration/officials`;
    const data: any = {
      size: size,
      page: page,
      filter: {
        officialType: officialType,
        membershipCategory: 'ANY',
        eventId: eventId,
        excludeIds: [0],
        subEventId
      },
      sort: {
        orders: [
          {
            direction: sort?.sortDir ?? "ASC",
            property: sort?.sortBy ?? "lastName"
          }
        ],
        "empty": !sort
      },
      offset: 0,
    };

    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  getPersons(
    search: string,
    page: number,
    size: number,
    minAge: number,
    eventId: number,
    orders?: any,
    nonAthletesOnly?: boolean,
    includeActiveMembershipsOnly?: boolean
  ) {
    const data: {
      page: number;
      size: number;
      filter: {
        excludeIds: number[];
        name: string;
        eventId: number;
        membershipCategory?: string;
        minAge?: number;
        includeActiveMembershipsOnly?: boolean;
      };
      offset: number;
      sort?: any;
    } = {
      page,
      size,
      filter: {
        excludeIds: [0],
        name: search,
        eventId: eventId,
        includeActiveMembershipsOnly: includeActiveMembershipsOnly,
      },
      offset: 0,
    };

    if (minAge !== -1) {
      data.filter.minAge = minAge;
    }

    if (orders !== undefined) {
      data.sort = orders;
    }

    if (nonAthletesOnly) {
      data["filter"]["membershipCategory"] = "NON_ATHLETE";
    }

    return this.requestCacheService.post(`${this.baseUrl}${personsFilterPATH}`, data);
  }

  async getSelfPerson(eventId: number) {
    let page = 0;
    let pageSize = 5;
    let totalPages = 1;
    try {
      while (page <= totalPages) {
        const response: any = await firstValueFrom(
          this.getPersons("", page, pageSize, -1, eventId)
        );
        if (response.items) {
          for (const i of response.items) {
            if (i.relation && i.relation === "SELF_PERSON") {
              return i;
            }
          }
        }
        page++;
        if (!response.totalPages) {
          return null;
        }
        totalPages = response.totalPages;
      }
      return null;
    } catch (e) {
      console.error(e);
    }
    return null;
  }

  addPerson(data: {
    email: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    gender: "MALE" | "FEMALE";
    photoUrl?: string;
    birthDate: string;
    country: any;
    address: string;
    zipCode: string;
    phoneNumber?: string;
    clubId?: number;
    relation: "SELF_PERSON" | "MINOR";
    // externalId: "string",
    // integrationType: "NONE",
    // isSelected: true,
  }) {
    this.invalidateAllowedOrderItemsCache();
    this.requestCacheService.invalidateAllForUrl(`${this.baseUrl}${personsFilterPATH}`);
    const path = `api/event/account/persons`;
    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  uploadImage(image: any, id: any): Observable<unknown> {
    const file = new File([image.blob], id + ".png", {
      type: image.blob.type,
    });

    const formData = new FormData();
    formData.append("file", file);

    this.requestCacheService.invalidateAllForUrl(`${this.baseUrl}${personsFilterPATH}`);

    const path = `api/event/account/persons/${id}/upload-picture`;
    return this.http.post(`${this.baseUrl}${path}`, formData);
  }

  uploadPersonDocument(personId: number, eventId: number, file: File): Observable<PersonDocument> {
    const formData = new FormData();
    formData.append("file", file);

    this.requestCacheService.invalidateAllForUrl(`${this.baseUrl}${personsFilterPATH}`);

    const path = `api/event/account/persons/${personId}/upload-document`;
    return this.http.post<PersonDocument>(`${this.baseUrl}${path}`, formData, { params: { eventId } });
  }

  deletePersonDocument(personId: number, documentId: number): Observable<boolean> {
    this.requestCacheService.invalidateAllForUrl(`${this.baseUrl}${personsFilterPATH}`);

    const path = `api/event/account/persons/${personId}/delete-document`;
    return this.http.delete<boolean>(`${this.baseUrl}${path}`, { params: { documentId } });
  }

  deleteImage(id: number): Observable<unknown> {
    const path = `api/event/account/persons/${id}/delete-picture`;
    return this.http.delete(`${this.baseUrl}${path}`);
  }

  updatePerson(data: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    gender: "MALE" | "FEMALE";
    photoUrl?: string;
    birthDate: string;
    country: any;
    address: string;
    zipCode: string;
    phoneNumber?: string;
    clubId?: number;
    relation: "SELF_PERSON" | "MINOR";
    ranks?: any[];
    // externalId: "string",
    // integrationType: "NONE",
    // isSelected: true,
  }) {
    this.invalidateAllowedOrderItemsCache();
    this.requestCacheService.invalidateAllForUrl(`${this.baseUrl}${personsFilterPATH}`);
    const path = `api/event/account/persons`;
    return this.http.put(`${this.baseUrl}${path}`, data);
  }

  deletePerson(data: {id: number, accountHolderId?: number}) {
    let options: any = { body: { id: data.id } };
    if (data.accountHolderId) {
      options.params = new HttpParams().set('accountHolderId', data.accountHolderId.toString());
    }
    return this.http.delete(`${this.baseUrl}api/event/account/persons`, options);
  }

  patchPerson(body: {
    id: number;
    overrideMembershipProgram?: "YOUTH" | "ADULT";
    admitFelonyCrime?: boolean;
    admitSexCrime?: boolean;
  }) {
    this.invalidateAllowedOrderItemsCache();
    this.requestCacheService.invalidateAllForUrl(`${this.baseUrl}${personsFilterPATH}`);
    const path = `api/event/account/persons`;
    return this.http.patch(`${this.baseUrl}${path}`, body);
  }

  invalidateAllowedOrderItemsCache() {
    this.requestCacheService.invalidateAllForUrlParts([`${this.baseUrl}api/orders/`, "allowed-order-items"])
  }

  getAllowedOrderItems(eventId: number, invalidateCache: boolean): Observable<unknown> {
    const path = `api/orders/${eventId}/allowed-order-items`;
    if (invalidateCache) {
      this.invalidateAllowedOrderItemsCache();
    }
    return this.requestCacheService.get(`${this.baseUrl}${path}`, undefined, 20 * 60 * 1000);
  }

  extendedCoverageAllowed(eventId: number, membershipId: number): Observable<unknown> {
    const path = `api/event/account/persons/${eventId}/allow-extend-coverage/${membershipId}`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  extendedCoverage(eventId: number, membershipId: number): Observable<unknown> {
    const path = `api/orders/${eventId}/extend-coverage/${membershipId}`;
    return this.http.post(`${this.baseUrl}${path}`, null);
  }

  extendedBulkCoverage(eventId: number, membershipIds: number[]): Observable<unknown> {
    const path = `api/orders/${eventId}/extend-coverages/bulk`;
    return this.http.post(`${this.baseUrl}${path}`, membershipIds);
  }

  getActiveOrder(eventId: number, isAAUSub = false): Observable<unknown> {
    const path = `api/orders/${eventId}/self/active` + (isAAUSub ? '/aau' : '');
    return this.http.get(`${this.baseUrl}${path}`);
  }

  rebuildEventLicenseOrder(id: any): Observable<unknown> {
    const path = `api/license/event/order/rebuild`;
    return this.http.post(`${this.baseUrl}${path}`, { eventLicenceId: id });
  }

  rebuildPracticeLicenseOrder(clubId: any, eventId: any): Observable<unknown> {
    const path = `api/license/practice/order/rebuild`;
    return this.http.post(`${this.baseUrl}${path}`, { clubId, eventId });
  }

  getPaidOrders(eventId: number): Observable<unknown> {
    const path = `api/orders/${eventId}/self/paid`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  hasPaidOrders(eventId: number): Observable<unknown> {
    const path = `api/orders/${eventId}/self/paid/refs`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  getPendingOrders(eventId: number): Observable<unknown> {
    const path = `api/orders/${eventId}/self/pending`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  insertRosterMembershipOrderItem(
    eventId: number,
    allowedItem: { itemType: string; personId: number; coverage: string, membershipTerm?: "TWO_YEARS" }
  ) {
    const path = `api/orders/${eventId}/items`;
    const data: any = {
      itemType: allowedItem.itemType,
      personId: allowedItem.personId,
      person: { id: allowedItem.personId },
      coverage: allowedItem.coverage,
      selected: true,
    };
    if (allowedItem.membershipTerm) {
      data.membershipTerm = allowedItem.membershipTerm
    }

    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  deleteOrderItem(eventId: number, itemId: number, personId: number) {
    const path = `api/orders/${eventId}/items`;

    const params = {
      personId: personId,
      person: { id: personId },
      id: itemId,
      selected: false,
      qty: 0,
    };

    return this.http.delete(`${this.baseUrl}${path}`, { body: params });
  }
  deleteBulkUploadOrder(eventId: number, params: any[]) {
    return this.http.delete(`${environment.APIURL}api/orders/${eventId}/items/batch`, { body: params });
  }
  addBulkUploadOrders(eventId: number, params: any[]) {
    return this.http.post(`${environment.APIURL}api/orders/${eventId}/items/batch`, params);
  }

  addToNCSA(personid: number) {
    const path = `api/event/integration/ncsa`;
    return this.http.put(`${this.baseUrl}${path}/${personid}`, {});
  }

  getOfficialsAllowedOrderItems(eventId: number): Observable<unknown> {
    const path = `api/orders/${eventId}/officials/allowed`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  getRegisterAsOfficialRegistrationFields(
    eventId: number
  ): Observable<unknown> {
    const path = `api/events/${eventId}/config/registration-fields`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  postRegistrationFieldsByPerson(eventId: number, personId: number, data: any) {
    const path = `api/events/${eventId}/config/registration-fields/person/${personId}`;
    return this.http.post(`${this.baseUrl}${path}`, data);
  }

  postEventFile(
    eventId: number,
    fileLocation: string,
    fileName: string,
    file: any
  ) {
    const path = `api/events/${eventId}/editor/event-file`;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileLocation", fileLocation);
    formData.append("fileName", fileName);

    return this.http.post(`${this.baseUrl}${path}`, formData);
  }

  getEventFiles(eventId: number): Observable<unknown> {
    const path = `api/events/${eventId}/editor/event-files`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  deleteEventFile(eventId: number, fileId: number) {
    const path = `api/events/${eventId}/editor/event-file/${fileId}`;
    const params = {
      eventId: eventId,
      fileId: fileId,
    };

    return this.http.delete(`${this.baseUrl}${path}`);
  }

  getGoogleWalletPass(
    email: string,
    membershipId: number
  ): Observable<unknown> {
    const path = `api/event/intergrations/google/wallet/generatePass`;
    const body = { email, membershipId };

    return this.http.post(`${this.baseUrl}${path}`, body);
  }

  getAppleWalletPass(email: string, membershipId: number): Observable<unknown> {
    const path = `api/event/intergrations/apple/wallet/generatePass`;
    const body = { email, membershipId };

    return this.http.post(`${this.baseUrl}${path}`, body);
  }

  getBlob(url: any) {
    return this.http.get(url, { responseType: 'blob' })
  }

  downLoadLink(body: { itemId: any; itemType: string; }): Observable<unknown> {
    const path = `api/downloads/download-link`;
    return this.http.post(`${this.baseUrl}${path}`, body);
  }

  verifyBulkUploadFile(eventId: number, file: File, params: { sportId: number; clubId?: number; }): Observable<Object> {
    const formData = new FormData();
    formData.append('file', file);

    let httpParams = new HttpParams()
      .set('sportId', String(params.sportId))

    if (params.clubId != null) {
      httpParams = httpParams.set('clubId', String(params.clubId));
    }

    this.invalidateAllowedOrderItemsCache();
    this.requestCacheService.invalidateAllForUrl(`${this.baseUrl}${personsFilterPATH}`);
    const path = `api/event/integration/aau/membership/${eventId}/import/bulk/verify`;
    return this.http.post(`${this.baseUrl}${path}`, formData, { params: httpParams });
  }

  verifyMembershipBulkLookup(file: File, leagueId: number = 40): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    const httpParams = new HttpParams().set('leagueId', String(leagueId));

    const path = `api/membership/lookup/bulk/verify`;
    return this.http.post(`${this.baseUrl}${path}`, formData, { params: httpParams });
  }

  uploadBulkData(eventId: number, bulkUploadId: number): Observable<any> {
    const path = `api/event/integration/aau/membership/${eventId}/import/bulk/upload?bulkUploadId=${bulkUploadId}`;
    return this.http.post(`${this.baseUrl}${path}`, {});
  }

  validateBulkRecord(bulkUploadId: number, body: any): Observable<unknown> {
    const path = `api/event/integration/aau/membership/import/bulk/${bulkUploadId}/validate/record?bulkUploadId=${bulkUploadId}`;
    return this.http.post(`${this.baseUrl}${path}`, body);
  }

  validateMembershipBulkRecord(bulkUploadId: number, body: any, leagueId: number = 40): Observable<unknown> {
    const httpParams = new HttpParams().set('leagueId', String(leagueId));
    const path = `api/membership/lookup/bulk/${bulkUploadId}/verify/record`;
    return this.http.post(`${this.baseUrl}${path}`, body, { params: httpParams });
  }

  ageAttestation(eventId: number, body: any): Observable<unknown> {
    const path = `api/event/account/persons/${eventId}/age-attestation`;
    return this.http.post(`${this.baseUrl}${path}`, body);
  }

  checkDigitalSign(body: any): Observable<unknown> {
    const path = `api/digital-signature/check`;
    return this.http.post(`${this.baseUrl}${path}`, body);
  }

  digitalSign(body: DigitalSignature): Observable<unknown> {
    const path = `api/digital-signature/sign`;
    return this.http.post(`${this.baseUrl}${path}`, body);
  }

  getContract(contractId: number): Observable<unknown> {
    const version = 0; // latest
    const path = `api/public/contract/${contractId}/${version}`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  submitPaymentDetails(token: string, lastFourDigits: string, orderId?: number, clubMembershipId?: number): Observable<unknown> {
    const path = `api/orders/stripe/payment`;
    let httpParams;
    if (orderId && clubMembershipId) {
      httpParams = new HttpParams()
        .set('stripeToken', token)
        .set('lastFourDigits', lastFourDigits)
        .set('orderId', orderId.toString())
        .set('clubMembershipId', clubMembershipId.toString());
    } else {
      httpParams = new HttpParams()
        .set('stripeToken', token)
        .set('lastFourDigits', lastFourDigits)
    }

    return this.http.post(`${this.baseUrl}${path}`, null, { params: httpParams });
  }

  getMyClubs() {
    const path = `api/clubmembership/self`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  filterClubMembers(
    page: number,
    pageSize: number,
    search: string,
    sorting: {
      direction: "ASC" | "DESC",
      property: "birthDate" | "lastName" | "firstName" | "type" | "program" | "coverage" | "gender"
    } | null,
    options?: {
      clubId?: number;
      gender?: "MALE" | "FEMALE" | "BOTH";
      ageGroupId?: number;
      sportId?: number;
      districtId?: number;
      membershipCategory?: "ATHLETE" | "NON_ATHLETE" | "ANY";
      personIds?: number[]; // works in combination with unaffiliated: true
    }, eventId?: number, unaffiliated?: boolean) {
    const { clubId, gender, ageGroupId, sportId, districtId, membershipCategory, personIds } = options || {};
    const params: any = {
      page: page,
      size: pageSize,
      filter: {
        excludeIds: [
          0
        ],
        name: "",
      },
      sort: {
        orders: [
          {
            direction: "ASC",
            property: "string"
          }
        ],
        empty: true
      },
      offset: 0
    };

    if (search) {
      params.filter.name = search;
    }
    if (clubId) {
      params.filter.clubId = clubId;
    }
    if (gender) {
      params.filter.gender = gender;
    }
    if (ageGroupId !== undefined && ageGroupId !== null) {
      params.filter.ageGroupId = ageGroupId;
    }
    if (sportId) {
      params.filter.sportId = sportId;
    }
    if (districtId) {
      params.filter.districtId = districtId;
    }
    if (membershipCategory) {
      params.filter.membershipCategory = membershipCategory;
    }
    if (sorting) {
      params.sort.orders[0] = sorting;
    }
    let queryParams: any = {};
    if (eventId) {
      queryParams.eventId = eventId;
    }
    if (unaffiliated) {
      queryParams.unaffiliated = unaffiliated;
      params.filter.personIds = personIds;
    }

    const path = `api/clubmembership/persons/filter`;
    return this.http.post(`${this.baseUrl}${path}`, params, {
      params: queryParams,
    });
  }

  filterClubTeamPersons(
    teamId: number,
    page: number,
    pageSize: number,
    search: string,
    sorting: { direction: "ASC" | "DESC", property: "teamRole" | "status" | "birthDate" | "lastName" | "firstName" } | null,
    options: {
      assignedNumber?: string,
      assignedNumberFrom?: number,
      assignedNumberTo?: number,
      gender?: "FEMALE" | "MALE" | "OTHER",
      membershipProgram?: "YOUTH" | "ADULT" | "BOTH" | "NONE",
      coverage?: "STANDARD" | "EXTENDED",
      sportId?: number,
      teamRole?: ClubTeamRole
    } = {}
  ) {
    const {
      assignedNumber,
      assignedNumberFrom,
      assignedNumberTo,
      gender,
      membershipProgram,
      coverage,
      sportId,
      teamRole
    } = options;
    const path = `api/clubteam/${teamId}/members/filter`;
    const data: FilteredRequestDTO<ClubTeamPersonFilter> = {
      page: page,
      size: pageSize,
      filter: {
        excludeIds: [0],
        name: search || "",
        assignedNumberFrom,
        assignedNumberTo,
        gender,
        membershipProgram,
        coverage,
        sportId,
        teamRole
      },
      sort: {
        orders: [
          {
            property: "teamRole",
            direction: "ASC"
          }
        ],
        empty: true
      },
      offset: 0
    };
    if (assignedNumber) {
      data.filter.assignedNumber = assignedNumber;
    }

    if (sorting) {
      data.sort!.orders[0] = sorting;
    }

    return this.http.post<FilteredResponseDTO<ClubTeamPersonFilter, ClubTeamPersonDTO>>(`${this.baseUrl}${path}`, data);
  }

  getClubTeamDetails(teamId: number) {
    const path = `api/clubteam/${teamId}`;
    return this.http.get<ClubTeamEditDTO>(`${this.baseUrl}${path}`);
  }

  getClubTeamMembersForTeam(teamId: number) {
    const path = `api/clubteam/${teamId}/members`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  createClubTeam(data: {
    id?: number,
    name: string,
    clubId?: number,
    members?: {
      id?: number,
      assignedNumber?: string,
      personId: number,
      role: 'ATHLETE' | 'COACH'
    }[],
    teamQualifications?: {
      id?: number;
      qualificationId: number;
      clubTeamId?: number;
      values: {
        name: string;
        type: string;
        required: "true" | "false";
        value: string | null;
      }[],
      status?: string;
    }[]
  }, leagueId: number) {
    const path = `api/clubteam`;

    return this.http.post(`${this.baseUrl}${path}`, data, {
      params: {
        leagueId
      },
    });
  }

  updateClubTeam(data: {
    id: number,
    name: string,
    clubId?: number,
    members?: {
      id?: number,
      assignedNumber?: string,
      position?: string,
      personId: number,
      role: 'ATHLETE' | 'COACH'
    }[]
  }) {
    const path = `api/clubteam`;
    return this.http.put<ClubTeamEditDTO>(`${this.baseUrl}${path}`, data);
  }

  updateClubTeamPersonsMetadata(data: {
    id: number,
    members?: {
      id?: number,
      assignedNumber?: string,
      position?: string
    }[]
  }, leagueId: number) {
    const path = `api/clubteam/members/metadata`;
    return this.http.put<ClubTeamEditDTO>(`${this.baseUrl}${path}`, data, {
      params: {
        leagueId
      }
    });
  }

  deleteClubTeam(teamId: number) {
    const path = `api/clubteam/${teamId}`;
    return this.http.delete(`${this.baseUrl}${path}`);
  }

  searchVenueFilter(eventId: number, params: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/venue/filter`;
    return this.http.post(`${this.baseUrl}${path}`, params);
  }

  searchPromoterFilter(eventId: number, params: any): Observable<unknown> {
    const path = `api/events/${eventId}/editor/promoter/filter`;
    return this.http.post(`${this.baseUrl}${path}`, params);
  }


}
